import { DateTime, type Duration } from 'luxon';

import Enums from '@core/types/api/Enums';
import type { NotificationMessage } from '@core/types/api/NotificationMessage/NotificationMessage';
import type { ReminderUserSettingSummary } from '@core/types/api/ReminderUserSettingSummary/ReminderUserSettingSummary';
import { FormatTenantDisplayDateTime } from '@core/utils/dateHelpers';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { pluralize } from '@core/utils/pluralize';
import {
  REMINDER_RECORD_TYPE_CONFIG,
  reminderBusinessDateResolver,
  reminderRecordSummary,
} from '@core/utils/reminderRecordTypes';
import { reminderTriggerDate } from '@core/utils/reminders';

export type NotificationCardAction = 'markAsRead' | 'delete' | 'edit';

export type NotificationCardDetail = {
  label: string;
  value: string;
};

export type NotificationCardItem = {
  id: string;
  kind: 'notification' | 'reminder';
  title: string;
  isUnread: boolean;
  details: NotificationCardDetail[];
  actions: NotificationCardAction[];
  triggerLabel?: string;

  record?: { type: Enums.ReminderUserSettingsRecordType; id: string; label: string };
};

const RECORD_TYPE_LOOKUP_ORDER: Enums.ReminderUserSettingsRecordType[] = [
  Enums.ReminderUserSettingsRecordType.Quote,
  Enums.ReminderUserSettingsRecordType.Project,
  Enums.ReminderUserSettingsRecordType.Activity,
];

export const reminderBusinessDate = (reminder: ReminderUserSettingSummary): DateTime | null => {
  const resolveBusinessDate = reminderBusinessDateResolver(
    reminder.recordType,
    reminder.recordBusinessDate,
  );
  return hasValue(resolveBusinessDate) ? resolveBusinessDate(reminder.recordData) : null;
};

const getReminderTriggerDate = (reminder: ReminderUserSettingSummary): DateTime | null => {
  if (hasValue(reminder.notifyAt)) {
    // notify_at holds tenant-local wall digits stored as UTC; count down to those.
    return reminder.notifyAt.isValid ? reminder.notifyAt.toUTC() : null;
  }

  const businessDate = reminderBusinessDate(reminder);
  const isDateOnly = reminder.recordType !== Enums.ReminderUserSettingsRecordType.Activity;
  return hasValue(businessDate) && hasValue(reminder.notifyBefore)
    ? reminderTriggerDate(businessDate, reminder.notifyBefore, isDateOnly)
    : null;
};

export const reminderTriggerLabel = (
  reminder: ReminderUserSettingSummary,
  now: DateTime = DateTime.now(),
): string | undefined => {
  const triggerDate = getReminderTriggerDate(reminder);
  if (!hasValue(triggerDate)) {
    return undefined;
  }

  const days = Math.round(triggerDate.startOf('day').diff(now.startOf('day'), 'days').days);
  if (days < 0) {
    return undefined;
  }
  if (days === 0) {
    return 'today';
  }
  return days === 1 ? 'in 1 day' : `in ${days} days`;
};

const NOTIFY_BEFORE_UNITS = ['years', 'months', 'weeks', 'days', 'hours', 'minutes'] as const;

// Not Duration.toHuman(): it needs Intl.ListFormat, which Hermes lacks, so mobile crashes on it.
export const formatNotifyBefore = (duration: Duration): string =>
  NOTIFY_BEFORE_UNITS.map((unit) => ({ unit, value: Math.round(duration[unit]) }))
    .filter(({ value }) => value > 0)
    .map(({ unit, value }) => `${value} ${pluralize(value, unit.slice(0, -1))}`)
    .join(', ');

const formatTriggerDate = (date: DateTime): string =>
  FormatTenantDisplayDateTime(date, DateTime.DATETIME_SHORT);

const notificationRecord = (data: NotificationMessage['data']): NotificationCardItem['record'] => {
  for (const recordType of RECORD_TYPE_LOOKUP_ORDER) {
    const { dataKey, label } = REMINDER_RECORD_TYPE_CONFIG[recordType];
    const recordId = data[dataKey];
    if (hasStringValue(recordId)) {
      return { type: recordType, id: recordId, label };
    }
  }
  return undefined;
};

export const notificationCardItem = (notification: NotificationMessage): NotificationCardItem => {
  const record = notificationRecord(notification.data);

  const details: NotificationCardDetail[] = [];
  if (hasStringValue(notification.message)) {
    details.push({ label: 'Note', value: notification.message });
  }
  details.push({
    label: 'Trigger Date',
    value: formatTriggerDate(notification.createdAt),
  });

  return {
    id: notification.id,
    kind: 'notification',
    title: notification.title,
    isUnread: !notification.isRead,
    details,
    actions: notification.isRead ? ['delete'] : ['markAsRead', 'delete'],
    record,
  };
};

export const reminderCardItem = (reminder: ReminderUserSettingSummary): NotificationCardItem => {
  const recordConfig = REMINDER_RECORD_TYPE_CONFIG[reminder.recordType];
  const recordSummary = reminderRecordSummary(reminder.recordType, reminder.recordData);
  const resolveBusinessDate = reminderBusinessDateResolver(
    reminder.recordType,
    reminder.recordBusinessDate,
  );

  const details: NotificationCardDetail[] = [];
  if (hasStringValue(reminder.note)) {
    details.push({ label: 'Note', value: reminder.note });
  }
  if (hasValue(resolveBusinessDate)) {
    const businessDate = resolveBusinessDate(reminder.recordData);
    details.push({
      label: `Business Date (${reminder.recordBusinessDate})`,
      value: hasValue(businessDate)
        ? FormatTenantDisplayDateTime(businessDate, DateTime.DATE_SHORT)
        : '-',
    });
  }

  if (hasValue(reminder.notifyBefore)) {
    details.push({
      label: 'Notify Before',
      value: formatNotifyBefore(reminder.notifyBefore),
    });
  } else if (hasValue(reminder.notifyAt)) {
    details.push({
      label: 'Notify At',
      // notify_at is a tenant-local wall clock stored as UTC; show those digits.
      value: FormatTenantDisplayDateTime(reminder.notifyAt.toUTC(), DateTime.DATETIME_SHORT),
    });
  }

  const record: NotificationCardItem['record'] = hasValue(recordSummary)
    ? {
        type: reminder.recordType,
        id: recordSummary.id,
        label: `${recordConfig.label}: ${recordSummary.title}`,
      }
    : undefined;

  return {
    id: reminder.id,
    kind: 'reminder',
    title: record?.label ?? `${recordConfig.label} ${reminder.recordBusinessDate}`,
    isUnread: true,
    triggerLabel: reminderTriggerLabel(reminder),
    details,
    actions: ['edit', 'delete'],
    record,
  };
};
